import { supabase } from '@/supabaseClient';
import { Article, ArticleStatus } from '@/types';

const normalizeText = (text: string): string => {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, ''); // Removes accents
};

const extractKeywords = (query: string, keepAccents: boolean = false): string[] => {
  if (!query) return [];
  
  const text = keepAccents ? query.toLowerCase() : normalizeText(query);
  
  // Replace punctuation and special characters with spaces, keeping Portuguese characters if keepAccents is true
  const cleaned = text.replace(/[^a-z0-9áàâãéèêíïóôõöúçñ\s]/gi, ' ');
  
  // Split words by whitespace
  const words = cleaned
    .split(/\s+/)
    .map(w => w.trim())
    .filter(w => w.length >= 3);

  // List of common Portuguese stopwords and helper words to ignore in scoring
  const stopwords = new Set([
    'eu', 'voce', 'ele', 'ela', 'nos', 'eles', 'elas',
    'de', 'do', 'da', 'dos', 'das', 'no', 'na', 'nos', 'nas', 'em',
    'um', 'uma', 'uns', 'umas', 'o', 'a', 'os', 'as',
    'que', 'se', 'com', 'por', 'para', 'como', 'mais', 'mas', 'ou',
    'e', 'qual', 'quais', 'quem', 'onde',
    'gostaria', 'saber', 'tente', 'encontrar', 'sobre', 'este', 'esta',
    'esse', 'essa', 'aquele', 'aquela', 'ter', 'haver', 'ser', 'estar'
  ]);

  return words.filter(word => !stopwords.has(word));
};

const loadJSZip = async (): Promise<any> => {
  if ((window as any).JSZip) return (window as any).JSZip;
  
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
    script.onload = () => resolve((window as any).JSZip);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const extractTextFromDocx = async (arrayBuffer: ArrayBuffer): Promise<string> => {
  try {
    const JSZip = await loadJSZip();
    const zip = await JSZip.loadAsync(arrayBuffer);
    
    // Main content of docx is in word/document.xml
    const docXmlFile = zip.file('word/document.xml');
    if (!docXmlFile) return '';
    
    const docXmlText = await docXmlFile.async('text');
    
    // Parse w:t tags which contain document text strings
    const textNodes: string[] = [];
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(docXmlText, 'application/xml');
    const tElements = xmlDoc.getElementsByTagName('w:t');
    
    for (let i = 0; i < tElements.length; i++) {
      const node = tElements[i];
      if (node.textContent) {
        textNodes.push(node.textContent);
      }
    }
    
    return textNodes.join(' ');
  } catch (err) {
    console.error("Error extracting text from DOCX:", err);
    return '';
  }
};

const extractTextFromXlsx = async (arrayBuffer: ArrayBuffer): Promise<string> => {
  try {
    const JSZip = await loadJSZip();
    const zip = await JSZip.loadAsync(arrayBuffer);
    
    // 1. Read shared strings
    let sharedStrings: string[] = [];
    const sharedStringsFile = zip.file('xl/sharedStrings.xml');
    if (sharedStringsFile) {
      const xmlText = await sharedStringsFile.async('text');
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, 'application/xml');
      const tElements = xmlDoc.getElementsByTagName('t');
      for (let i = 0; i < tElements.length; i++) {
        sharedStrings.push(tElements[i].textContent || '');
      }
    }
    
    // 2. Read worksheets
    const worksheetTexts: string[] = [];
    const files = Object.keys(zip.files);
    const sheetFiles = files.filter(f => f.startsWith('xl/worksheets/sheet'));
    
    for (const sheetFile of sheetFiles) {
      const xmlText = await zip.files[sheetFile].async('text');
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, 'application/xml');
      
      const cellElements = xmlDoc.getElementsByTagName('c');
      const sheetCells: string[] = [];
      
      for (let i = 0; i < cellElements.length; i++) {
        const cell = cellElements[i];
        const type = cell.getAttribute('t');
        const vElement = cell.getElementsByTagName('v')[0];
        
        if (vElement && vElement.textContent) {
          const val = vElement.textContent;
          if (type === 's') {
            const strIdx = parseInt(val, 10);
            if (!isNaN(strIdx) && sharedStrings[strIdx]) {
              sheetCells.push(sharedStrings[strIdx]);
            }
          } else {
            sheetCells.push(val);
          }
        }
      }
      
      if (sheetCells.length > 0) {
        worksheetTexts.push(`[Planilha ${sheetFile.split('/').pop()?.replace('.xml', '')}]: ${sheetCells.join(', ')}`);
      }
    }
    
    return worksheetTexts.join('\n');
  } catch (err) {
    console.error("Error extracting text from XLSX:", err);
    return '';
  }
};

export const generateWikiResponse = async (
  query: string, 
  history: { role: 'user' | 'model'; parts: { text: string }[] }[],
  _legacyArticles?: Article[],
  hasOfficeAttachments?: boolean,
  userId?: string
): Promise<string> => {

  // 1. Query Supabase for relevant articles using a hybrid and flexible search approach
  let searchResults: Article[] = [];
  try {
    const rawKeywords = extractKeywords(query, true);
    const normalizedKeywords = extractKeywords(query, false);
    
    // Combine both accented and unaccented keywords to maximize FTS matching
    const combinedKeywords = Array.from(new Set([...rawKeywords, ...normalizedKeywords]));

    const finalKeywords = normalizedKeywords.length > 0 
      ? normalizedKeywords 
      : normalizeText(query).split(/[^a-z0-9]+/).filter(w => w.length >= 2);

    let ftsData: any[] = [];
    if (combinedKeywords.length > 0) {
      // Join keywords with OR to fetch all matching candidates
      const ftsQuery = combinedKeywords.join(' OR ');
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('status', 'Aprovado')
        .textSearch('fts_vector', ftsQuery, {
          config: 'portuguese',
          type: 'websearch'
        })
        .limit(20);

      if (error) {
        console.error("Supabase FTS error:", error);
      } else if (data) {
        ftsData = data;
      }
    }

    // Always fetch the 15 most recent approved articles as well, to ensure new articles are checked
    const { data: recentData, error: recentError } = await supabase
      .from('articles')
      .select('*')
      .eq('status', 'Aprovado')
      .order('updated_at', { ascending: false })
      .limit(15);

    if (recentError) {
      console.error("Supabase recent fetch error:", recentError);
    }

    // Combine and remove duplicates
    const allCandidates = [...ftsData, ...(recentData || [])];
    const uniqueCandidatesMap = new Map<string, any>();
    allCandidates.forEach(c => {
      if (c && c.id) {
        uniqueCandidatesMap.set(c.id, c);
      }
    });
    const uniqueCandidates = Array.from(uniqueCandidatesMap.values());

    // Map database snake_case fields to app's camelCase Article type
    const mappedArticles: Article[] = uniqueCandidates.map((a: any) => ({
      id: a.id,
      title: a.title,
      content: a.content,
      authorId: a.author_id,
      authorName: a.author_name,
      sectorId: a.sector_id,
      sectorName: a.sector_name,
      status: a.status as ArticleStatus,
      createdAt: a.created_at,
      updatedAt: a.updated_at,
      tags: a.tags || [],
      attachments: a.attachments || []
    }));

    // Score and rank candidates based on user keywords match
    const scoredArticles = mappedArticles.map(doc => {
      const titleNorm = normalizeText(doc.title);
      const contentNorm = normalizeText(doc.content);
      const tagsNorm = (doc.tags || []).map((t: string) => normalizeText(t)).join(' ');
      const attachmentsNorm = (doc.attachments || [])
        .map((att: any) => normalizeText(att.name || ''))
        .join(' ');

      let score = 0;

      finalKeywords.forEach(keyword => {
        // 1. Match in Title (weight: 15)
        if (titleNorm.includes(keyword)) {
          score += 15;
          // Extra points for exact word match in title
          const regex = new RegExp(`\\b${keyword}\\b`, 'g');
          const titleMatches = titleNorm.match(regex);
          if (titleMatches) {
            score += titleMatches.length * 10;
          }
        }

        // 2. Match in Content (weight: 5)
        if (contentNorm.includes(keyword)) {
          score += 5;
          // Extra points for exact word match in content
          const regex = new RegExp(`\\b${keyword}\\b`, 'g');
          const contentMatches = contentNorm.match(regex);
          if (contentMatches) {
            score += contentMatches.length * 3;
          }
        }

        // 3. Match in Tags (weight: 8)
        if (tagsNorm.includes(keyword)) {
          score += 8;
        }

        // 4. Match in Attachment Filenames (weight: 12)
        if (attachmentsNorm.includes(keyword)) {
          score += 12;
        }
      });

      return { doc, score };
    });

    // Sort by score descending, then by updatedAt descending for ties
    scoredArticles.sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      return new Date(b.doc.updatedAt).getTime() - new Date(a.doc.updatedAt).getTime();
    });

    // Select top 5
    searchResults = scoredArticles.slice(0, 5).map(item => item.doc);
  } catch (searchErr) {
    console.error("Failed to perform hybrid search query:", searchErr);
  }

  // 2. Prepare Context Text
  const contextText = searchResults.map(doc => 
    `---
ID: ${doc.id}
DOCUMENTO: ${doc.title}
SETOR: ${doc.sectorName}
CONTEÚDO TEXTUAL:
${doc.content}
---`
  ).join('\n\n');

  // 3. Prepare Attachments (Limit to top 2 to keep request fast and light)
  const contentBlocks: any[] = [];
  let filesCount = 0;
  const MAX_FILES = 2;

  for (const doc of searchResults) {
    if (doc.attachments && doc.attachments.length > 0) {
      for (const att of doc.attachments) {
        if (filesCount >= MAX_FILES) break;

        const isImage = att.type.includes('image');
        const isPdf = att.type.includes('pdf');
        const isWord = att.type.includes('wordprocessingml.document') || att.type.includes('msword') || att.name.toLowerCase().endsWith('.docx') || att.name.toLowerCase().endsWith('.doc');
        const isExcel = att.type.includes('spreadsheetml.sheet') || att.type.includes('ms-excel') || att.name.toLowerCase().endsWith('.xlsx') || att.name.toLowerCase().endsWith('.xls');

        if (isImage || isPdf || isWord || isExcel) {
          try {
            if (isWord || isExcel) {
              // Office file extraction (text-only RAG)
              let arrayBuffer: ArrayBuffer;
              if (att.url.startsWith('data:')) {
                const base64Data = att.url.split(',')[1];
                const binaryString = window.atob(base64Data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }
                arrayBuffer = bytes.buffer;
              } else {
                const resp = await fetch(att.url);
                arrayBuffer = await resp.arrayBuffer();
              }

              const extractedText = isWord 
                ? await extractTextFromDocx(arrayBuffer)
                : await extractTextFromXlsx(arrayBuffer);

              if (extractedText.trim()) {
                contentBlocks.push({
                  type: 'text',
                  text: `---
CONTEÚDO TEXTUAL EXTRAÍDO DO ARQUIVO ${isWord ? 'WORD' : 'EXCEL'} ANEXADO ("${att.name}") DO DOCUMENTO "${doc.title}":
${extractedText}
---`
                });
                filesCount++;
              }
            } else {
              // Image or PDF binary handling
              let base64Data = '';
              if (att.url.startsWith('data:')) {
                base64Data = att.url.split(',')[1];
              } else {
                const resp = await fetch(att.url);
                const blob = await resp.blob();
                base64Data = await new Promise<string>((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    const result = reader.result as string;
                    resolve(result.split(',')[1]);
                  };
                  reader.onerror = reject;
                  reader.readAsDataURL(blob);
                });
              }

              if (isImage) {
                contentBlocks.push({
                  type: 'image',
                  source: {
                    type: 'base64',
                    media_type: att.type as any,
                    data: base64Data
                  }
                });
                contentBlocks.push({
                  type: 'text',
                  text: `(Imagem acima anexada ao documento: "${doc.title}")`
                });
                filesCount++;
              } else if (isPdf) {
                contentBlocks.push({
                  type: 'document',
                  source: {
                    type: 'base64',
                    media_type: 'application/pdf',
                    data: base64Data
                  }
                });
                contentBlocks.push({
                  type: 'text',
                  text: `(PDF acima anexado ao documento: "${doc.title}")`
                });
                filesCount++;
              }
            }
          } catch (e) {
            console.warn("Erro ao processar anexo para Claude:", att.name, e);
          }
        }
      }
    }
    if (filesCount >= MAX_FILES) break;
  }

  // 4. Construct System Instruction
  const systemInstruction = `Você é a Soes, a IA oficial da SÓES.
Sua função é responder dúvidas dos colaboradores baseando-se nos documentos de texto E nos arquivos anexados (PDFs/Imagens/documentos Word/Excel) fornecidos.

DIRETRIZES DE LINGUAGEM E GRAMÁTICA:
1. Diferencie SINGULAR e PLURAL:
   - Pergunta no SINGULAR -> Resposta focada e específica.
   - Pergunta no PLURAL -> Lista completa ou visão geral.
2. Mantenha a concordância gramatical.

INSTRUÇÃO DE CONTEÚDO:
- Você recebeu textos explicativos via contexto de sistema.
- Você TAMBÉM pode receber arquivos (PDFs, Imagens, Textos extraídos de Word/Excel) na mensagem.
- Analise AMBOS. Muitas vezes a resposta está detalhada dentro de um anexo.
- Se encontrar a informação em um anexo, cite: "Conforme o anexo do documento [Nome do Documento]..."
- Responda de forma direta e profissional.
- Se a resposta não estiver nem no texto nem nos anexos, diga que não encontrou de forma curta e educada.
- Não invente informações.
- Responda em português.

CONTEXTO DE TEXTO DOS DOCUMENTOS DISPONÍVEIS:
${contextText}`;

  // 5. Build Messages history for Anthropic
  const messages: any[] = [];

  if (history && history.length > 0) {
    history.forEach((h: any) => {
      const role = h.role === 'model' ? 'assistant' : 'user';
      const text = h.parts?.[0]?.text || h.text || '';
      if (text.trim()) {
        messages.push({ role, content: text });
      }
    });
  }

  // Add the current user query, optionally with file attachments
  if (contentBlocks.length > 0) {
    contentBlocks.push({
      type: 'text',
      text: query
    });
    messages.push({
      role: 'user',
      content: contentBlocks
    });
  } else {
    messages.push({
      role: 'user',
      content: query
    });
  }

  // 6. Chama a Edge Function no Supabase (Execução segura no backend)
  try {
    const { data, error } = await supabase.functions.invoke('claude-proxy', {
      body: {
        systemInstruction,
        messages,
        query,
        userId
      }
    });

    if (error) {
      console.error("Erro da Edge Function do Supabase:", error);
      throw new Error(error.message || "Erro desconhecido na chamada da Edge Function.");
    }

    if (data && data.text) {
      return data.text;
    }
    
    if (data && data.error) {
      throw new Error(data.error);
    }

    return "A IA não retornou texto. Tente novamente.";
  } catch (error: any) {
    console.error("Claude Service Error:", error);
    
    let genericErrorMessage = "Falha na comunicação com a IA Claude. Tente novamente.";
    if (hasOfficeAttachments) {
      genericErrorMessage += "\n\nDICA: Arquivos Word/Excel complexos ou muito grandes podem ser difíceis para a IA processar diretamente. Tente converter para PDF ou extrair o texto relevante.";
    }
    if (error.message) {
      genericErrorMessage += ` (Detalhe: ${error.message.substring(0, 120)})`;
    }
    return genericErrorMessage;
  }
};
